import React, { useEffect } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import configureStore from 'store/store';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import { initializeNavigation } from 'utils/navigation-utils';
import IntlProvider from 'containers/util/intl-provider-container';
import HomePage from 'containers/feature/homepage-container';
import ProjectCreationPage from 'containers/feature/project-creation/project-creation-container';
import ProjectEditPage from 'containers/feature/project-edit/project-edit-container';
import StatementCreationPage from 'containers/feature/statement-creation/statement-creation-container';
import StatementEditPage from 'containers/feature/statement-edit/statement-edit-container';
import StatementContentPage from 'containers/feature/statement-content/statement-content-container';
import OmniaEngagementPage from 'components/feature/omnia/omnia-engagement-page-component';
import SessionTimeoutPage from 'pages/session-timeout-page';
import TieoutPage from 'pages/tieout-page';
import LogoutPage from 'pages/logout-page';
import Unauthorized from 'containers/unauthorized-container';
import PublicRoute from 'components/routes/public-route-component';
import ProtectedRoute from 'components/routes/protected-route-component';
import RouteNotFound from 'components/routes/404-route-not-found-component';
import AuthSuccess from 'components/app/auth-success-component';
import 'styles/app-styles.scss';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SessionTimeout from 'components/session-timeout/session-timeout-component';
import AdminModulePage from 'pages/admin-module/admin-module-page';
import SideBySideViewPage from 'pages/side-by-side-compare/side-by-side-view';
import ReportPreviewPage from 'pages/report-preview/report-preview';
import AdobeTracker from 'pages/adobe-tracker';
import DAUserSettings from './feature/disclosure-analytics/da-user-settings-component';

function AppContent() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    initializeNavigation(navigate, dispatch);
  }, [navigate, dispatch]);

  return (
    <>
      <span className="cookielink" style={{ display: 'none' }}>
        <button id="ot-sdk-btn" className="ot-sdk-show-settings">
          Cookie-setting button - not for display
        </button>
      </span>
      <AdobeTracker />
      <DAUserSettings />
      <Routes>
        <Route
          path="/authsuccess"
          element={<PublicRoute component={AuthSuccess} />}
        />
        <Route
          path="/unauthorized"
          element={<PublicRoute component={Unauthorized} />}
        />
        <Route
          path={'/omnia'}
          element={<ProtectedRoute component={HomePage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.LOGOUT_PAGE}
          element={<PublicRoute component={LogoutPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.SESSION_TIMEOUT}
          element={<PublicRoute component={SessionTimeoutPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.HOME_PAGE}
          element={<ProtectedRoute component={HomePage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.TIEOUT_PAGE}
          element={<ProtectedRoute component={TieoutPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.PROJECT_CREATION_PAGE}
          element={<ProtectedRoute component={ProjectCreationPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.PROJECT_EDIT_PAGE}
          element={<ProtectedRoute component={ProjectEditPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.STATEMENT_CREATION}
          element={<ProtectedRoute component={StatementCreationPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.STATEMENT_EDIT_PAGE}
          element={<ProtectedRoute component={StatementEditPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.STATEMENT_CONTENT_PAGE}
          element={<ProtectedRoute component={StatementContentPage} />}
        />
        {/**TODO: Once the split page is properly set up, do not map
         * this route to the statement content page anymore, but to a separate
         * statement split page.
         */}
        <Route
          path={ROUTE_CONSTANTS.STATEMENT_SPLIT_PAGE}
          element={<ProtectedRoute component={StatementContentPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.OMNIA_ENGAGEMENT_PAGE}
          element={<ProtectedRoute component={OmniaEngagementPage} />}
        />
        <Route
          path={ROUTE_CONSTANTS.ADMIN_MODULE_PAGE}
          element={<ProtectedRoute component={AdminModulePage} />}
        />
        <Route
          path={`${ROUTE_CONSTANTS.STATEMENT_CONTENT_PAGE}${ROUTE_CONSTANTS.SIDE_BY_SIDE}`}
          element={<ProtectedRoute component={SideBySideViewPage} />}
        />
        <Route
          path={`${ROUTE_CONSTANTS.STATEMENT_CONTENT_PAGE}${ROUTE_CONSTANTS.REPORT_PREVIEW}`}
          element={<ProtectedRoute component={ReportPreviewPage} />}
        />

        <Route path="*" element={<PublicRoute component={RouteNotFound} />} />
      </Routes>
      <ToastContainer
        className="Toast-notification"
        transition={Slide}
        autoClose={5000}
        hideProgressBar
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        closeButton={false}
      />
      <SessionTimeout />
    </>
  );
}

function App() {
  return (
    <Provider store={configureStore({})}>
      <IntlProvider>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }} // Added these flags to avoid console warnings and future
        >
          <AppContent />
        </BrowserRouter>
      </IntlProvider>
    </Provider>
  );
}

export default App;
